// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthToken, getAuthUser } from '../../utils/auth';
import './Seo.css';

function Seo() {
    const [projects, setProjects] = useState([]);
    const [audits, setAudits] = useState([]);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [dashboard, setDashboard] = useState(null);
    const [gscStatus, setGscStatus] = useState(null);
    const [rankHistory, setRankHistory] = useState([]);
    const [competitors, setCompetitors] = useState([]);

    const [projectForm, setProjectForm] = useState({
        name: '',
        targetUrl: '',
        targetKeywords: '',
        notes: '',
    });

    const [siteInput, setSiteInput] = useState({
        url: '',
        title: '',
        description: '',
        h1Count: 1,
        wordCount: 500,
        imagesWithoutAlt: 0,
        httpLinksCount: 0,
    });

    const [keywordInput, setKeywordInput] = useState({ keyword: '' });
    const [backlinkInput, setBacklinkInput] = useState({ domain: '' });
    const [rankInput, setRankInput] = useState({ keyword: '', position: 20, location: 'global', device: 'desktop', searchEngine: 'google', url: '' });
    const [technicalInput, setTechnicalInput] = useState({ pagesCrawled: 250, brokenLinks: 6, duplicateTitles: 8, missingMeta: 11, slowPages: 14, blockedPages: 2, orphanPages: 5 });
    const [competitorInput, setCompetitorInput] = useState({ domain: '', competitors: '' });
    const [briefInput, setBriefInput] = useState({ keyword: '', audience: 'marketers', intent: 'informational', wordCount: 1200, competitors: '' });

    const [siteResult, setSiteResult] = useState(null);
    const [keywordResult, setKeywordResult] = useState(null);
    const [backlinkResult, setBacklinkResult] = useState(null);
    const [technicalResult, setTechnicalResult] = useState(null);
    const [competitorResult, setCompetitorResult] = useState(null);
    const [briefResult, setBriefResult] = useState(null);

    const token = useMemo(() => getAuthToken(), []);
    const isAdmin = useMemo(() => {
        try {
            const u = getAuthUser() || {};
            return u.role === 'admin';
        } catch { return false; }
    }, []);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
    }), [token]);

    const scopedAudits = useMemo(() => {
        if (!selectedProjectId) return audits;
        return audits.filter((a) => (a.projectId || '').toString() === selectedProjectId);
    }, [audits, selectedProjectId]);

    const latestScores = useMemo(() => {
        var latestSite = scopedAudits.find((a) => a.tool === 'site-analyzer' && typeof a.score === 'number');
        var latestTech = scopedAudits.find((a) => a.tool === 'technical-audit' && typeof a.score === 'number');
        return {
            site: latestSite ? latestSite.score : null,
            technical: latestTech ? latestTech.score : null,
        };
    }, [scopedAudits]);

    const loadData = async () => {
        if (!token) return;
        try {
            const fetchList = [
                fetch(API_ENDPOINTS.SEO.PROJECTS, { headers }),
                fetch(API_ENDPOINTS.SEO.AUDITS + '?limit=100', { headers }),
                fetch(API_ENDPOINTS.SEO.DASHBOARD + (selectedProjectId ? '?projectId=' + encodeURIComponent(selectedProjectId) : ''), { headers }),
            ];
            if (isAdmin) fetchList.push(fetch(API_ENDPOINTS.SEO.GSC_STATUS, { headers }));

            const [projectRes, auditRes, dashboardRes, gscStatusRes] = await Promise.all(fetchList);

            if (projectRes.ok) {
                const projectData = await projectRes.json();
                setProjects(Array.isArray(projectData) ? projectData : []);
            }

            if (auditRes.ok) {
                const auditData = await auditRes.json();
                setAudits(Array.isArray(auditData) ? auditData : []);
            }

            if (dashboardRes.ok) {
                const dashboardData = await dashboardRes.json();
                setDashboard(dashboardData || null);
            }

            if (isAdmin && gscStatusRes && gscStatusRes.ok) {
                const gscData = await gscStatusRes.json();
                setGscStatus(gscData || null);
            }

            if (selectedProjectId) {
                const [rankRes, compRes] = await Promise.all([
                    fetch(API_ENDPOINTS.SEO.RANK_HISTORY + '?projectId=' + encodeURIComponent(selectedProjectId) + '&limit=120', { headers }),
                    fetch(API_ENDPOINTS.SEO.GET_COMPETITORS(selectedProjectId), { headers }),
                ]);

                if (rankRes.ok) {
                    const rankData = await rankRes.json();
                    setRankHistory(Array.isArray(rankData) ? rankData : []);
                }

                if (compRes.ok) {
                    const compData = await compRes.json();
                    setCompetitors(Array.isArray(compData.competitors) ? compData.competitors : []);
                }
            } else {
                setRankHistory([]);
                setCompetitors([]);
            }
        } catch (err) {
            console.error('Failed to load SEO data:', err);
        }
    };

    useEffect(() => {
        loadData();
    }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const createProject = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        if (!projectForm.name.trim()) {
            setError('Project name is required');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: projectForm.name,
                targetUrl: projectForm.targetUrl,
                targetKeywords: projectForm.targetKeywords
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean),
                notes: projectForm.notes,
            };

            const res = await fetch(API_ENDPOINTS.SEO.PROJECTS, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errJson = await res.json().catch(() => ({}));
                throw new Error(errJson.message || 'Unable to create project');
            }

            setProjectForm({ name: '', targetUrl: '', targetKeywords: '', notes: '' });
            setMessage('Project created successfully.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Unable to create project');
        } finally {
            setLoading(false);
        }
    };

    const runSiteAnalysis = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_SITE, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...siteInput, projectId: selectedProjectId || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Site analysis failed');
            setSiteResult(data.analysis);
            setMessage('Site analysis completed.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Site analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const runKeywordAnalysis = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_KEYWORD, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...keywordInput, projectId: selectedProjectId || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Keyword analysis failed');
            setKeywordResult(data.result);
            setMessage('Keyword exploration completed.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Keyword analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const runBacklinkAnalysis = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_BACKLINK, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...backlinkInput, projectId: selectedProjectId || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Backlink analysis failed');
            setBacklinkResult(data.result);
            setMessage('Backlink snapshot generated.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Backlink analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const saveRankSnapshot = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.RANK_SNAPSHOT, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...rankInput, projectId: selectedProjectId || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Unable to save rank snapshot');
            setMessage('Rank snapshot saved.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Unable to save rank snapshot');
        } finally {
            setLoading(false);
        }
    };

    const runTechnicalAudit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_TECHNICAL, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...technicalInput, projectId: selectedProjectId || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Technical audit failed');
            setTechnicalResult(data.result);
            setMessage('Technical audit completed.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Technical audit failed');
        } finally {
            setLoading(false);
        }
    };

    const runCompetitorGap = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const competitorArray = competitorInput.competitors.split(',').map((c) => c.trim()).filter(Boolean);

            if (selectedProjectId) {
                const saveRes = await fetch(API_ENDPOINTS.SEO.SAVE_COMPETITORS(selectedProjectId), {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ competitors: competitorArray }),
                });
                if (!saveRes.ok) {
                    const saveErr = await saveRes.json().catch(() => ({}));
                    throw new Error(saveErr.message || 'Unable to save competitors');
                }
            }

            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_COMPETITOR_GAP, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    projectId: selectedProjectId || undefined,
                    domain: competitorInput.domain,
                    competitors: competitorArray,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Competitor gap analysis failed');
            setCompetitorResult(data.result);
            setCompetitors(competitorArray);
            setMessage('Competitor gap analysis completed.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Competitor gap analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const generateContentBrief = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.SEO.ANALYZE_CONTENT_BRIEF, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    ...briefInput,
                    competitors: briefInput.competitors.split(',').map((v) => v.trim()).filter(Boolean),
                    projectId: selectedProjectId || undefined,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Content brief generation failed');
            setBriefResult(data.result);
            setMessage('Content brief generated.');
            await loadData();
        } catch (err) {
            setError(err.message || 'Content brief generation failed');
        } finally {
            setLoading(false);
        }
    };

    const exportReport = async () => {
        try {
            const endpoint = API_ENDPOINTS.SEO.EXPORT_REPORT + (selectedProjectId ? '?projectId=' + encodeURIComponent(selectedProjectId) : '');
            const res = await fetch(endpoint, { headers: { Authorization: 'Bearer ' + token } });
            if (!res.ok) throw new Error('Report export failed');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = selectedProjectId ? 'seo-report-project.csv' : 'seo-report-all.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Report export failed');
        }
    };

    const tabButtons = [
        { key: 'dashboard', label: 'Dashboard' },
        { key: 'site', label: 'Site' },
        { key: 'keyword', label: 'Keyword' },
        { key: 'backlink', label: 'Backlink' },
        { key: 'rank', label: 'Rank' },
        { key: 'technical', label: 'Technical' },
        { key: 'competitor', label: 'Competitor' },
        { key: 'brief', label: 'Brief' },
        { key: 'reports', label: 'Reports' },
    ];

    useEffect(() => {
        if (!message) return undefined;
        const timer = setTimeout(() => setMessage(''), 5000);
        return () => clearTimeout(timer);
    }, [message]);

    return (
        <>
            <Helmet>
                <title>SEO Fort - App Store | Hari Vishnu</title>
                <meta name="description" content="SEO workspace with site checks, keyword ideas, and backlink snapshots for subscribed users." />
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href="https://apps.harivishnu.in/seo" />
            </Helmet>

            <div className="seo-shell mx-auto my-[1rem] w-[min(96%,1400px)]">
                <div className="seo-header rounded-[14px] border border-slider bg-bg px-[14px] py-[12px]">
                    <h1>SEO Fort</h1>
                    <p>Project dashboard, rankings, technical audits, competitors, content briefs, and reports.</p>
                </div>

                {error && <div className="seo-error mt-[10px] rounded-[10px] border border-[#fecaca] bg-[#fff5f5] px-[12px] py-[10px] text-[#dc2626]">{error}</div>}
                {message && (
                    <div className="seo-info mt-[10px] flex items-center justify-between gap-[8px] rounded-[10px] border border-[#bfdbfe] bg-[#eff6ff] px-[12px] py-[10px] text-[#1d4ed8]" role="status" aria-live="polite">
                        <span>{message}</span>
                        <button type="button" className="seo-alert-close flex h-[24px] w-[24px] items-center justify-center rounded-full border-none bg-transparent text-[#1d4ed8] hover:bg-blue-100" onClick={() => setMessage('')} aria-label="Close message">×</button>
                    </div>
                )}

                <div className="seo-grid mt-[12px] grid gap-[12px] xl:grid-cols-[320px_minmax(0,1fr)_300px]">
                    <aside className="seo-card seo-left-panel rounded-[14px] border border-slider bg-bg p-[12px]">
                        <h2>Create Project</h2>
                        <form onSubmit={createProject} className="seo-form mt-[8px] flex flex-col gap-[8px]">
                            <input
                                type="text"
                                placeholder="Project name"
                                className="rounded-[8px] border border-slider bg-bg px-[10px] py-[8px] outline-none"
                                value={projectForm.name}
                                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Target URL"
                                className="rounded-[8px] border border-slider bg-bg px-[10px] py-[8px] outline-none"
                                value={projectForm.targetUrl}
                                onChange={(e) => setProjectForm({ ...projectForm, targetUrl: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Keywords (comma separated)"
                                className="rounded-[8px] border border-slider bg-bg px-[10px] py-[8px] outline-none"
                                value={projectForm.targetKeywords}
                                onChange={(e) => setProjectForm({ ...projectForm, targetKeywords: e.target.value })}
                            />
                            <textarea
                                placeholder="Notes"
                                rows={3}
                                className="rounded-[8px] border border-slider bg-bg px-[10px] py-[8px] outline-none"
                                value={projectForm.notes}
                                onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                            />
                            <button type="submit" className="rounded-[10px] border border-primary-cyan bg-primary-cyan px-[12px] py-[9px] font-[700] text-white disabled:opacity-60" disabled={loading}>Save Project</button>
                        </form>

                        <div className="seo-field-stack">
                            <label className="seo-label">Active Project</label>
                            <select
                                className="seo-select"
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                            >
                                <option value="">All Projects</option>
                                {projects.map((p) => (
                                    <option key={p._id} value={p._id}>{p.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="seo-project-list mt-[10px] space-y-[8px]">
                            {projects.length === 0 ? <p>No projects yet.</p> : projects.map((p) => (
                                <div key={p._id} className="seo-project-item rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <strong>{p.name}</strong>
                                    <span>{p.targetUrl || 'No URL configured'}</span>
                                </div>
                            ))}
                        </div>
                    </aside>

                    <main className="seo-card seo-main-panel rounded-[14px] border border-slider bg-bg p-[12px]">
                        <div className="seo-tabs flex flex-wrap gap-[6px]">
                            {tabButtons.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={(activeTab === tab.key ? 'active border-primary-cyan bg-primary-cyan text-white' : 'border-slider bg-bg text-text hover:bg-black/5') + ' rounded-[10px] border px-[10px] py-[7px] text-[0.9rem] font-[700]'}
                                    onClick={() => setActiveTab(tab.key)}
                                    type="button"
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'dashboard' && (
                            <div className="seo-dashboard-grid mt-[10px] grid gap-[8px] sm:grid-cols-2 xl:grid-cols-3">
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Total Projects</span>
                                    <strong>{dashboard?.projects ?? 0}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Recent Audits</span>
                                    <strong>{dashboard?.recentAudits ?? 0}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Avg Site Score</span>
                                    <strong>{dashboard?.avgSiteScore ?? '-'}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Avg Technical Score</span>
                                    <strong>{dashboard?.avgTechnicalScore ?? '-'}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Tracked Keywords</span>
                                    <strong>{dashboard?.trackedKeywords ?? 0}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Latest Site Score</span>
                                    <strong>{latestScores.site ?? '-'}</strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Plan</span>
                                    <strong>{dashboard?.planId || 'free'}</strong>
                                </div>
                                {isAdmin && (
                                    <div className="seo-kpi seo-kpi--admin rounded-[10px] border border-[#bfdbfe] bg-[#eff6ff] px-[10px] py-[8px]">
                                        <span>GSC <span className="seo-admin-badge">Admin Only</span></span>
                                        <strong>{gscStatus?.configured ? 'Connected' : 'Not Connected'}</strong>
                                    </div>
                                )}
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Site Audit Quota (Today)</span>
                                    <strong>
                                        {(dashboard?.dailyUsage?.siteAnalyses ?? 0)}/{(dashboard?.dailyLimits?.siteAnalyses ?? '-')}
                                    </strong>
                                </div>
                                <div className="seo-kpi rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <span>Keyword Quota (Today)</span>
                                    <strong>
                                        {(dashboard?.dailyUsage?.keywordQueries ?? 0)}/{(dashboard?.dailyLimits?.keywordQueries ?? '-')}
                                    </strong>
                                </div>
                            </div>
                        )}

                        {activeTab === 'site' && (
                            <form onSubmit={runSiteAnalysis} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <input type="text" placeholder="URL" value={siteInput.url} onChange={(e) => setSiteInput({ ...siteInput, url: e.target.value })} />
                                <input type="text" placeholder="Page title" value={siteInput.title} onChange={(e) => setSiteInput({ ...siteInput, title: e.target.value })} />
                                <textarea rows={2} placeholder="Meta description" value={siteInput.description} onChange={(e) => setSiteInput({ ...siteInput, description: e.target.value })} />
                                <div className="seo-inline-fields">
                                    <div className="seo-field-stack">
                                        <label className="seo-label">H1 Count</label>
                                        <input type="number" min="0" placeholder="H1 count" value={siteInput.h1Count} onChange={(e) => setSiteInput({ ...siteInput, h1Count: Number(e.target.value) })} />
                                    </div>
                                    <div className="seo-field-stack">
                                        <label className="seo-label">Word Count</label>
                                        <input type="number" min="0" placeholder="Word count" value={siteInput.wordCount} onChange={(e) => setSiteInput({ ...siteInput, wordCount: Number(e.target.value) })} />
                                    </div>
                                </div>
                                <div className="seo-inline-fields">
                                    <div className="seo-field-stack">
                                        <label className="seo-label">Images Missing Alt</label>
                                        <input type="number" min="0" placeholder="Images without alt" value={siteInput.imagesWithoutAlt} onChange={(e) => setSiteInput({ ...siteInput, imagesWithoutAlt: Number(e.target.value) })} />
                                    </div>
                                    <div className="seo-field-stack">
                                        <label className="seo-label">HTTP Links Count</label>
                                        <input type="number" min="0" placeholder="HTTP links count" value={siteInput.httpLinksCount} onChange={(e) => setSiteInput({ ...siteInput, httpLinksCount: Number(e.target.value) })} />
                                    </div>
                                </div>
                                <button type="submit" disabled={loading}>Run Site Analysis</button>
                            </form>
                        )}

                        {activeTab === 'keyword' && (
                            <form onSubmit={runKeywordAnalysis} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <input type="text" placeholder="Primary keyword" value={keywordInput.keyword} onChange={(e) => setKeywordInput({ keyword: e.target.value })} />
                                <button type="submit" disabled={loading}>Explore Keyword</button>
                            </form>
                        )}

                        {activeTab === 'backlink' && (
                            <form onSubmit={runBacklinkAnalysis} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <input type="text" placeholder="Domain" value={backlinkInput.domain} onChange={(e) => setBacklinkInput({ domain: e.target.value })} />
                                <button type="submit" disabled={loading}>Check Backlinks</button>
                            </form>
                        )}

                        {activeTab === 'rank' && (
                            <>
                                <form onSubmit={saveRankSnapshot} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                    <input type="text" placeholder="Keyword" value={rankInput.keyword} onChange={(e) => setRankInput({ ...rankInput, keyword: e.target.value })} />
                                    <div className="seo-inline-fields">
                                        <input type="number" min="1" placeholder="Position" value={rankInput.position} onChange={(e) => setRankInput({ ...rankInput, position: Number(e.target.value) })} />
                                        <input type="text" placeholder="Target URL" value={rankInput.url} onChange={(e) => setRankInput({ ...rankInput, url: e.target.value })} />
                                    </div>
                                    <div className="seo-inline-fields">
                                        <select className="seo-select" value={rankInput.location} onChange={(e) => setRankInput({ ...rankInput, location: e.target.value })}>
                                            <option value="global">Global</option>
                                            <option value="india">India</option>
                                            <option value="usa">USA</option>
                                        </select>
                                        <select className="seo-select" value={rankInput.device} onChange={(e) => setRankInput({ ...rankInput, device: e.target.value })}>
                                            <option value="desktop">Desktop</option>
                                            <option value="mobile">Mobile</option>
                                        </select>
                                    </div>
                                    <button type="submit" disabled={loading}>Save Rank Snapshot</button>
                                </form>

                                <div className="seo-table-wrap">
                                    <table className="seo-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Keyword</th>
                                                <th>Position</th>
                                                <th>Location</th>
                                                <th>Device</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rankHistory.length === 0 ? (
                                                <tr><td colSpan="5">No rank snapshots yet.</td></tr>
                                            ) : rankHistory.slice(0, 20).map((item) => (
                                                <tr key={item._id}>
                                                    <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                                                    <td>{item.keyword}</td>
                                                    <td>{item.position}</td>
                                                    <td>{item.location}</td>
                                                    <td>{item.device}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {activeTab === 'technical' && (
                            <form onSubmit={runTechnicalAudit} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <div className="seo-inline-fields">
                                    <input type="number" min="0" placeholder="Pages crawled" value={technicalInput.pagesCrawled} onChange={(e) => setTechnicalInput({ ...technicalInput, pagesCrawled: Number(e.target.value) })} />
                                    <input type="number" min="0" placeholder="Broken links" value={technicalInput.brokenLinks} onChange={(e) => setTechnicalInput({ ...technicalInput, brokenLinks: Number(e.target.value) })} />
                                </div>
                                <div className="seo-inline-fields">
                                    <input type="number" min="0" placeholder="Duplicate titles" value={technicalInput.duplicateTitles} onChange={(e) => setTechnicalInput({ ...technicalInput, duplicateTitles: Number(e.target.value) })} />
                                    <input type="number" min="0" placeholder="Missing meta" value={technicalInput.missingMeta} onChange={(e) => setTechnicalInput({ ...technicalInput, missingMeta: Number(e.target.value) })} />
                                </div>
                                <div className="seo-inline-fields">
                                    <input type="number" min="0" placeholder="Slow pages" value={technicalInput.slowPages} onChange={(e) => setTechnicalInput({ ...technicalInput, slowPages: Number(e.target.value) })} />
                                    <input type="number" min="0" placeholder="Blocked pages" value={technicalInput.blockedPages} onChange={(e) => setTechnicalInput({ ...technicalInput, blockedPages: Number(e.target.value) })} />
                                </div>
                                <input type="number" min="0" placeholder="Orphan pages" value={technicalInput.orphanPages} onChange={(e) => setTechnicalInput({ ...technicalInput, orphanPages: Number(e.target.value) })} />
                                <button type="submit" disabled={loading}>Run Technical Audit</button>
                            </form>
                        )}

                        {activeTab === 'competitor' && (
                            <form onSubmit={runCompetitorGap} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <input type="text" placeholder="Your domain" value={competitorInput.domain} onChange={(e) => setCompetitorInput({ ...competitorInput, domain: e.target.value })} />
                                <input type="text" placeholder="Competitors (comma separated domains)" value={competitorInput.competitors} onChange={(e) => setCompetitorInput({ ...competitorInput, competitors: e.target.value })} />
                                <button type="submit" disabled={loading}>Analyze Competitor Gap</button>
                                {competitors.length > 0 && <p className="seo-muted">Saved competitors: {competitors.join(', ')}</p>}
                            </form>
                        )}

                        {activeTab === 'brief' && (
                            <form onSubmit={generateContentBrief} className="seo-form mt-[10px] flex flex-col gap-[8px]">
                                <input type="text" placeholder="Primary keyword" value={briefInput.keyword} onChange={(e) => setBriefInput({ ...briefInput, keyword: e.target.value })} />
                                <div className="seo-inline-fields">
                                    <input type="text" placeholder="Audience" value={briefInput.audience} onChange={(e) => setBriefInput({ ...briefInput, audience: e.target.value })} />
                                    <select className="seo-select" value={briefInput.intent} onChange={(e) => setBriefInput({ ...briefInput, intent: e.target.value })}>
                                        <option value="informational">Informational</option>
                                        <option value="commercial">Commercial</option>
                                        <option value="transactional">Transactional</option>
                                    </select>
                                </div>
                                <input type="number" min="600" placeholder="Word count" value={briefInput.wordCount} onChange={(e) => setBriefInput({ ...briefInput, wordCount: Number(e.target.value) })} />
                                <input type="text" placeholder="Competitors (optional, comma separated)" value={briefInput.competitors} onChange={(e) => setBriefInput({ ...briefInput, competitors: e.target.value })} />
                                <button type="submit" disabled={loading}>Generate Content Brief</button>
                            </form>
                        )}

                        {activeTab === 'reports' && (
                            <div className="seo-report-actions mt-[10px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <button type="button" onClick={exportReport} className="seo-action-btn rounded-[10px] border border-slider bg-bg px-[12px] py-[8px] font-[700] hover:border-primary-cyan">Export CSV Report</button>
                                <p className="seo-muted">Exports all or selected project audits in CSV format.</p>
                            </div>
                        )}

                        {siteResult && activeTab === 'site' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Site Analysis Report</h3>
                                <div className="seo-report-summary">
                                    <div className="seo-report-chip"><span>SEO Score</span><strong>{siteResult.score}</strong></div>
                                    <div className="seo-report-chip"><span>Title Length</span><strong>{siteResult.summary?.titleLength ?? 0}</strong></div>
                                    <div className="seo-report-chip"><span>Description Length</span><strong>{siteResult.summary?.descriptionLength ?? 0}</strong></div>
                                    <div className="seo-report-chip"><span>Word Count</span><strong>{siteResult.summary?.wordCount ?? 0}</strong></div>
                                </div>
                                <div className="seo-report-list">
                                    {(siteResult.issues || []).length === 0 ? (
                                        <div className="seo-report-item seo-report-item--good">No issues detected.</div>
                                    ) : (
                                        siteResult.issues.map((issue, idx) => (
                                            <div key={idx} className={`seo-report-item seo-report-item--${issue.severity || 'notice'}`}>
                                                <strong>{(issue.severity || 'notice').toUpperCase()}</strong>
                                                <span>{issue.title}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {keywordResult && activeTab === 'keyword' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Keyword Report</h3>
                                <div className="seo-report-summary">
                                    <div className="seo-report-chip"><span>Keyword</span><strong>{keywordResult.keyword}</strong></div>
                                    <div className="seo-report-chip"><span>Volume</span><strong>{keywordResult.estimatedVolume.toLocaleString()}</strong></div>
                                    <div className="seo-report-chip"><span>Difficulty</span><strong>{keywordResult.estimatedDifficulty}</strong></div>
                                    <div className="seo-report-chip"><span>CPC</span><strong>{keywordResult.estimatedCpc}</strong></div>
                                </div>
                                <div className="seo-report-list">
                                    {(keywordResult.suggestions || []).map((s, idx) => (
                                        <div key={idx} className="seo-report-item seo-report-item--info">
                                            <strong>SUGGESTION</strong>
                                            <span>{s}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {backlinkResult && activeTab === 'backlink' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Backlink Report</h3>
                                <div className="seo-report-summary">
                                    <div className="seo-report-chip"><span>Domain</span><strong>{backlinkResult.domain}</strong></div>
                                    <div className="seo-report-chip"><span>Domain Rating</span><strong>{backlinkResult.domainRating}</strong></div>
                                    <div className="seo-report-chip"><span>Referring Domains</span><strong>{backlinkResult.referringDomains.toLocaleString()}</strong></div>
                                    <div className="seo-report-chip"><span>Dofollow Ratio</span><strong>{backlinkResult.dofollowRatio}%</strong></div>
                                </div>
                            </div>
                        )}

                        {technicalResult && activeTab === 'technical' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Technical Audit Report</h3>
                                <div className="seo-report-summary">
                                    <div className="seo-report-chip"><span>Technical Score</span><strong>{technicalResult.score}</strong></div>
                                    <div className="seo-report-chip"><span>Pages Crawled</span><strong>{technicalResult.pagesCrawled}</strong></div>
                                    <div className="seo-report-chip"><span>Healthy Pages</span><strong>{technicalResult.summary?.healthyPages ?? 0}</strong></div>
                                    <div className="seo-report-chip"><span>Indexed Estimate</span><strong>{technicalResult.summary?.indexedEstimate ?? 0}</strong></div>
                                </div>
                                <div className="seo-report-list">
                                    {(technicalResult.issues || []).map((issue, idx) => (
                                        <div key={idx} className={`seo-report-item seo-report-item--${issue.severity || 'notice'}`}>
                                            <strong>{(issue.severity || 'notice').toUpperCase()}</strong>
                                            <span>{issue.title} ({issue.count})</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {competitorResult && activeTab === 'competitor' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Competitor Opportunities</h3>
                                <div className="seo-report-list">
                                    {competitorResult.opportunities.map((item, idx) => (
                                        <div key={idx} className="seo-report-item seo-report-item--info">
                                            <strong>{item.competitor}</strong>
                                            <span>Missing Keywords: {item.missingKeywords}, Gap Score: {item.contentGapScore}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {briefResult && activeTab === 'brief' && (
                            <div className="seo-result mt-[12px] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <h3>Content Brief: {briefResult.keyword}</h3>
                                <div className="seo-report-summary">
                                    <div className="seo-report-chip"><span>Word Count</span><strong>{briefResult.recommendedWordCount}</strong></div>
                                    <div className="seo-report-chip"><span>Audience</span><strong>{briefResult.audience}</strong></div>
                                    <div className="seo-report-chip"><span>Intent</span><strong>{briefResult.intent}</strong></div>
                                </div>
                                <div className="seo-report-list">
                                    {briefResult.h2Outline.map((line, idx) => (
                                        <div key={idx} className="seo-report-item seo-report-item--info">
                                            <strong>H2</strong>
                                            <span>{line}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </main>

                    <aside className="seo-card seo-right-panel rounded-[14px] border border-slider bg-bg p-[12px]">
                        <h2>Recent Audits</h2>
                        <div className="seo-audit-list mt-[8px] space-y-[8px]">
                            {scopedAudits.length === 0 ? <p>No audits yet.</p> : scopedAudits.slice(0, 30).map((a) => (
                                <div key={a._id} className="seo-audit-item rounded-[10px] border border-slider bg-bg px-[10px] py-[8px]">
                                    <strong>{a.tool}</strong>
                                    <span>{new Date(a.createdAt).toLocaleString()}</span>
                                    {a.score !== null && a.score !== undefined && <span>Score: {a.score}</span>}
                                </div>
                            ))}
                        </div>
                    </aside>
                </div>
            </div>
        </>
    );
}

export default Seo;
